"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Spinner, Typography } from "@/shared/ui/atoms";
import { Loading } from "@/shared/ui/organisms";
import { NoDataTemplates } from "@/shared/ui/templates";
import { getSearchResultClient } from "@/entities/search/api/search.client";
import { useSearchUiStore } from "@/features/search/model/ui/store";
import { FilterValue } from "@/shared/types/type/search";
import { resolveSearchRegionIds } from "@/features/search-result/lib/resolveSearchRegionIds";
// 파라미터 해석·queryKey 는 서버 프리페치(page)와 공유하는 계약 — searchListQuery 한곳에서만 수정
import { parseSearchListParams, searchListQueryKey } from "@/features/search-result/lib/searchListQuery";
import { useSearchResultAnchorScrollRestore } from "@/features/search-result/lib/useSearchResultAnchorScrollRestore";
import { getTreatmentCategoryName } from "@/features/search-result/lib/getTreatmentCategoryName";
import { pickLocalizedText } from "@/features/search-result/lib/pickLocalizedText";
import { SearchResultHospitalCard } from "@/features/search-result/ui/molecules";
import { buildHospitalShortPath } from "@/entities/hospital/lib/detail-route";
interface SearchResultListProps {
  treatmentList: FilterValue[];
}
const SearchResultList = t0 => {
  const $ = _c(44);
  const {
    treatmentList
  } = t0;
  const t = useTranslations();
  const locale = useLocale();
  const loadMoreRef = useRef(null);
  const params = useSearchParams();
  const {
    regionList
  } = useSearchUiStore();
  let t1;
  if ($[0] !== params) {
    t1 = key => params?.get(key) ?? null;
    $[0] = params;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    keyword,
    treatmentIds,
    rawRegionIds
  } = parseSearchListParams(t1);
  const regionIds = resolveSearchRegionIds(regionList, rawRegionIds);
  const [showLoading, setShowLoading] = useState(false);
  const querySignature = `q=${keyword}|r=${regionIds.join(",")}|t=${treatmentIds.join(",")}`;
  const t2 = searchListQueryKey(keyword, regionIds, treatmentIds);
  let t3;
  if ($[2] !== keyword || $[3] !== regionIds || $[4] !== treatmentIds) {
    t3 = t4 => {
      const {
        pageParam: t5
      } = t4;
      const pageParam = t5 === undefined ? 1 : t5;
      return getSearchResultClient({
        keyword,
        regionIds,
        categoryIds: treatmentIds,
        page: pageParam as number,
        size: 10
      });
    };
    $[2] = keyword;
    $[3] = regionIds;
    $[4] = treatmentIds;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      initialPageParam: 1,
      getNextPageParam: _temp,
      select: _temp3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data: data_0,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useInfiniteQuery(t4);
  let t5;
  if ($[9] !== fetchNextPage || $[10] !== hasNextPage || $[11] !== isFetchingNextPage || $[12] !== querySignature) {
    t5 = {
      signature: querySignature,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage
    };
    $[9] = fetchNextPage;
    $[10] = hasNextPage;
    $[11] = isFetchingNextPage;
    $[12] = querySignature;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const {
    saveAnchor
  } = useSearchResultAnchorScrollRestore(t5);
  let t6;
  let t7;
  if ($[14] !== fetchNextPage || $[15] !== hasNextPage) {
    t6 = () => {
      if (!loadMoreRef.current || !hasNextPage) {
        return;
      }
      const observer = new IntersectionObserver(t8 => {
        const [entry] = t8;
        if (entry.isIntersecting) {
          fetchNextPage();
        }
      }, {
        rootMargin: "300px"
      });
      observer.observe(loadMoreRef.current);
      return () => observer.disconnect();
    };
    t7 = [fetchNextPage, hasNextPage];
    $[14] = fetchNextPage;
    $[15] = hasNextPage;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useEffect(t6, t7);
  let t8;
  if ($[18] !== isLoading || $[19] !== setShowLoading) {
    t8 = () => {
      const timer = setTimeout(() => setShowLoading(isLoading), isLoading ? 300 : 0);
      return () => clearTimeout(timer);
    };
    $[18] = isLoading;
    $[19] = setShowLoading;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== isLoading) {
    t9 = [isLoading];
    $[21] = isLoading;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  useEffect(t8, t9);
  if (showLoading) {
    let t10;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Loading />;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    return t10;
  }
  if (!isLoading && data_0?.hospitals.length === 0) {
    let t10;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <NoDataTemplates pageName="search" />;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    return t10;
  }
  const resultCount = data_0?.totalHits ?? 0;
  let t10;
  if ($[25] !== resultCount || $[26] !== t) {
    t10 = t("list.resultCount", {
      count: resultCount
    });
    $[25] = resultCount;
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10) {
    t11 = <div className="flex h-[18px] items-center px-1"><Typography size={14} lineHeight={18} className="font-medium text-[#9A9AA3]">{t10}</Typography></div>;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== data_0?.hospitals || $[31] !== locale || $[32] !== saveAnchor || $[33] !== treatmentList) {
    t12 = data_0?.hospitals.map((item, index) => <Link key={`${item.hospitalOriginId ?? index}`} href={buildHospitalShortPath(locale, item.hospitalOriginId)} className="block cursor-pointer" data-search-result-anchor={item.hospitalOriginId} onClickCapture={e => saveAnchor(item.hospitalOriginId, e.currentTarget)}><SearchResultHospitalCard name={pickLocalizedText(item.hospitalName, locale)} address={item.jibunAddress ?? pickLocalizedText(item.address, locale)} treatmentName={getTreatmentCategoryName(item.treatmentCategoryIds, treatmentList)} score={item.score} reviewCount={item.reviewCount} thumbnailUrl={item.mainImage?.s3Url} /></Link>);
    $[30] = data_0?.hospitals;
    $[31] = locale;
    $[32] = saveAnchor;
    $[33] = treatmentList;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t12) {
    t13 = <div className="flex flex-col gap-2">{t12}</div>;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div ref={loadMoreRef} style={{
      height: 1
    }} />;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== isFetchingNextPage) {
    t15 = isFetchingNextPage && <div className="flex items-center justify-center py-2"><Spinner size={42} /></div>;
    $[38] = isFetchingNextPage;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t11 || $[41] !== t13 || $[42] !== t15) {
    t16 = <div className="flex flex-col gap-2.5 bg-[#F4F4F7] px-4 pb-4 pt-3.5">{t11}{t13}{t14}{t15}</div>;
    $[40] = t11;
    $[41] = t13;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
};
export default SearchResultList;
function _temp(lastPage, pages) {
  if (lastPage.result.hospitals.length < 10) {
    return;
  }
  return pages.length + 1;
}
function _temp2(page) {
  return page.result.hospitals;
}
function _temp3(data) {
  return {
    hospitals: data.pages.flatMap(_temp2),
    totalHits: data.pages[0]?.result.totalHits ?? 0
  };
}